import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { BASE_OPTIONS } from './chartBase';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_RESULTADO_ATENCION } from './mockDataEjemplos';
import { filasResultadoAtencion, CLAVE_SIN_CLASIFICAR, CLAVE_TRAMITE_EN_CURSO } from './calculosReportes';

// Orden y colores de los resultados viven en calculosReportes.js: los comparten este gráfico,
// TendenciaPorTipoChart y la exportación a PDF/Excel. Desde V224 el resultado es un catálogo
// editable, así que el orden es el que manda el backend (cantidad desc) con "Sin clasificar" al
// final, y cada resultado nuevo que agregue el estudio recibe su propio tono claro.

/**
 * Resultado de la atención — en qué terminó cada atención de recepción: se abrió un
 * trámite en el momento, el cliente va a volver con documentación (carpeta a futuro),
 * el caso no es viable, o no se cargó el dato (turnos históricos, anteriores a la
 * incorporación de este campo — ver TurnoRecepcionEntity.resultadoAtencion).
 * @param {{data: Array<{resultado: string, resultadoLabel: string, cantidad: number, porcentaje: number}>}} props
 */
const ResultadoAtencionChart = forwardRef(({ data, compacto = false, titulo = 'Resultado de la atención', subtitulo = null }, ref) => {
    // El backend ya devuelve ordenado por cantidad desc; filasResultadoAtencion baja
    // "Sin clasificar" al final (no es un desenlace sino la ausencia del dato) y asigna colores.
    const todas = useMemo(() => filasResultadoAtencion(data), [data]);

    // Quien vino por un trámite en curso no es una consulta: no compite con los otros desenlaces
    // ni comparte denominador con ellos. Sale del anillo y de los porcentajes, y se muestra abajo
    // con su cantidad — sacarlo de la pantalla escondería un tercio del trabajo del mostrador.
    const enCurso = useMemo(() => todas.find((f) => f.resultado === CLAVE_TRAMITE_EN_CURSO), [todas]);
    const filas = useMemo(() => todas.filter((f) => f.resultado !== CLAVE_TRAMITE_EN_CURSO), [todas]);

    // El anillo y su número central son las CONSULTAS. El total de atenciones se lee sumándole las
    // de seguimiento, que están escritas justo abajo.
    const total = useMemo(() => filas.reduce((acc, f) => acc + f.cantidad, 0), [filas]);
    const sinClasificar = filas.find((f) => f.resultado === CLAVE_SIN_CLASIFICAR);
    const pctSinClasificar = sinClasificar && total ? (sinClasificar.cantidad / total) * 100 : 0;

    const chartData = {
        labels: filas.map((f) => f.resultadoLabel),
        datasets: [{
            data: filas.map((f) => f.cantidad),
            backgroundColor: filas.map((f) => f.color),
            borderColor: '#fff',
            borderWidth: 3,
            hoverOffset: 8,
        }],
    };

    const options = {
        ...BASE_OPTIONS,
        cutout: '62%',
        plugins: { ...BASE_OPTIONS.plugins, legend: { display: false } },
    };

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Resultado de la atención"
                        queMuestra="Te dice en qué terminó, en concreto, cada atención de recepción: no solo si vino o no vino el cliente, sino qué pasó una vez que se lo atendió."
                        ejemploVisual={<ResultadoAtencionChart compacto data={MOCK_RESULTADO_ATENCION} />}
                        comoLeerlo={[
                            'Verde (Inicia ahora) = se abrió el trámite en el momento, el mejor desenlace posible.',
                            'Azul (Carpeta a futuro) = el cliente va a volver con documentación — un desenlace todavía pendiente, ni bueno ni malo.',
                            'Ámbar (No quiso dejar carpeta) = el caso era viable, pero la persona no dejó la carpeta.',
                            'Rojo (No viable) = el caso no prospera con lo que trajo el cliente.',
                            'Gris oscuro (Vino por un trámite en curso) = ya era cliente y no abrió nada en esta visita: vino a traer un papel, a un turno médico, a tomar vista. No es una consulta, así que no entra en el porcentaje — se muestra aparte con su cantidad.',
                            'Gris claro (Sin clasificar) = se cerró sin resultado ni carpeta (común en turnos históricos, previos a este campo).',
                            'El número en el centro de la dona son las CONSULTAS: las atenciones que sí podían terminar en uno de estos desenlaces. Las de seguimiento se cuentan aparte, debajo.',
                        ]}
                        analisis="Inicia ahora (40%) y Carpeta a futuro (30%) juntos son el 70% del total — la mayoría de las atenciones sí tienen un camino hacia adelante, aunque no siempre inmediato. No viable (20%) no es necesariamente un problema de gestión: puede reflejar bien la calidad real de la demanda que entra por turnos. Sin clasificar (10%) todavía es manejable; si ese gris creciera con el tiempo, empezaría a tapar si el resto de esta lectura es confiable."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>{titulo}</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        {subtitulo || 'En qué terminó cada atención de las personas que se presentaron: se abrió trámite ahora, vuelve con documentación, el caso no es viable, o no se cargó el dato al finalizar la atención.'}
                    </Typography>
                </>
            )}
            {total === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No hay turnos en el período seleccionado.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: compacto ? 12 : 20 }}>
                    <Box sx={{ position: 'relative', width: compacto ? 110 : 180, height: compacto ? 110 : 180, flexShrink: 0 }}>
                        <Doughnut ref={ref} data={chartData} options={options} />
                        <Box sx={{
                            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
                        }}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 23, fontWeight: 700, color: COLOR.negro, lineHeight: 1 }}>
                                {total.toLocaleString('es-AR')}
                            </Typography>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: COLOR.grisTexto }}>
                                {enCurso ? 'consultas' : 'atenciones'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                        {filas.map((f) => (
                            <Box key={f.resultado} sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '2px', background: f.color, marginTop: '3px', flexShrink: 0 }} />
                                <Box>
                                    {/* Sin porcentaje va el desenlace que está fuera del denominador
                                        ("Vino por un trámite en curso"): no comparte base con los
                                        otros, así que se lo muestra con su cantidad y nada más. */}
                                    <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.negro }}>
                                        {f.resultadoLabel}
                                        {f.porcentaje === null || f.porcentaje === undefined
                                            ? null
                                            : <> <b>{Number(f.porcentaje).toFixed(0)}%</b></>}
                                    </Typography>
                                    <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto }}>
                                        {f.cantidad.toLocaleString('es-AR')} atenciones
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            {enCurso && enCurso.cantidad > 0 && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '8px', marginTop: compacto ? '10px' : '16px',
                    paddingTop: compacto ? '10px' : '14px', borderTop: '1px solid #f1f5f9',
                }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', background: enCurso.color, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto }}>
                        {enCurso.resultadoLabel}: <b style={{ color: COLOR.negro }}>{enCurso.cantidad.toLocaleString('es-AR')}</b>
                        {!compacto && ' — ya eran clientes y no abrieron trámite en esta visita, así que no entran en los porcentajes de arriba.'}
                    </Typography>
                </Box>
            )}
            {!compacto && sinClasificar && pctSinClasificar >= 40 && (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto, marginTop: '16px' }}>
                    "Sin clasificar" es el {pctSinClasificar.toFixed(0)}% del total — normal en los primeros días tras
                    incorporar este campo, o si todavía hay turnos históricos en el rango elegido.
                </Typography>
            )}
        </Card>
    );
});

export default ResultadoAtencionChart;
